import { SAlert, SBox, SDialog, SLoader } from "@avalara/skylab-sdk/react";
import React from "react";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import type { IncomingCompany } from "./types";

/** A company in this (destination) account. */
export interface TargetCompany {
    value: string;
    label: string;
}

/** Destination company value -> chosen incoming (package) company id as a string ("" = none). */
export type CompanyMapping = Record<string, string>;

interface ImportCompanyMappingDialogProps {
    open: boolean;
    sourceAccountName?: string | null;
    incoming: IncomingCompany[];
    targets: TargetCompany[];
    isLoadingTargets?: boolean;
    mapping: CompanyMapping;
    /** Destination companies actually mapped *and* still in the current list — what the POST sends. */
    mappedTotal: number;
    onChange: (targetValue: string, incomingId: string) => void;
    onRefreshTargets: () => void;
    onCancel: () => void;
    onContinue: () => void;
}

const DIALOG_ID = "dataTransferMappingDialog";

const ImportCompanyMappingDialog: React.FC<ImportCompanyMappingDialogProps> = ({
    open,
    sourceAccountName,
    incoming,
    targets,
    isLoadingTargets = false,
    mapping,
    mappedTotal,
    onChange,
    onRefreshTargets,
    onCancel,
    onContinue,
}) => {
    const usedIncoming = new Set(Object.values(mapping).filter(Boolean));

    return (
        <SDialog
            id={DIALOG_ID}
            open={open}
            data-testid={testIDs.dataTransfer.mappingDialog}
            onS-dismiss={(e: CustomEvent<{ id?: string }>) => {
                if (e.detail?.id === DIALOG_ID) onCancel();
            }}
            aria-modal="true">
            <div slot="header">Configure imported data</div>
            <div slot="body">
                <SBox>
                    <p className="margin-top-none">
                        For each of your companies that should receive data, choose the company in
                        the package to import from
                        {sourceAccountName ? ` (exported from ${sourceAccountName})` : ""}. Leave a
                        company unmapped to keep its data unchanged.
                    </p>
                    <p>
                        Don&apos;t see a company listed? It has to exist in this account before you
                        can map it. Create it first, then{" "}
                        <button
                            type="button"
                            className="link"
                            disabled={isLoadingTargets}
                            onClick={onRefreshTargets}>
                            refresh the list
                        </button>
                        .
                    </p>
                    {isLoadingTargets ? (
                        <div
                            role="status"
                            aria-live="polite"
                            data-testid={testIDs.dataTransfer.mappingLoading}>
                            <SLoader loading /> Loading companies…
                        </div>
                    ) : targets.length === 0 ? (
                        <SAlert
                            status="warning"
                            noDismiss
                            data-testid={testIDs.dataTransfer.mappingEmpty}>
                            This account has no companies yet. Create one, then refresh the list.
                        </SAlert>
                    ) : (
                        <table className="data-transfer-mapping-table">
                            <thead>
                                <tr>
                                    <th scope="col">Your company</th>
                                    <th scope="col">Incoming company</th>
                                </tr>
                            </thead>
                            <tbody>
                                {targets.map(target => {
                                    const selectId = `data-transfer-map-${target.value}`;
                                    const current = mapping[target.value] ?? "";
                                    return (
                                        <tr key={target.value}>
                                            <td>{target.label}</td>
                                            <td>
                                                <label className="sr-only" htmlFor={selectId}>
                                                    Incoming company for {target.label}
                                                </label>
                                                <select
                                                    id={selectId}
                                                    data-testid={`${testIDs.dataTransfer.mappingSelect}-${target.value}`}
                                                    value={current}
                                                    onChange={e =>
                                                        onChange(target.value, e.target.value)
                                                    }>
                                                    <option value="">Select company</option>
                                                    {incoming.map(company => (
                                                        <option
                                                            key={company.id}
                                                            value={String(company.id)}
                                                            disabled={
                                                                usedIncoming.has(
                                                                    String(company.id)
                                                                ) && current !== String(company.id)
                                                            }>
                                                            {company.id} - {company.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </SBox>
            </div>
            <div slot="footer">
                <span className="data-transfer-count">
                    {mappedTotal} of {targets.length} mapped
                </span>
                <button type="button" className="secondary" onClick={onCancel}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="primary"
                    disabled={mappedTotal === 0}
                    data-testid={testIDs.dataTransfer.mappingContinue}
                    onClick={onContinue}>
                    Continue
                </button>
            </div>
        </SDialog>
    );
};

export default ImportCompanyMappingDialog;
